import { PageAnalysisConfigsService } from '@halo/services/page-analysis-configs.service'
import { Container } from 'typescript-ioc'
import { useEffect, useState } from 'react'
import { IHydratedPageAnalysisConfig } from '@halo/interfaces/hydrated-page-analysis-config'
import { classnames } from '@halo/_utils/classnames'
import { Button, PageHeader } from '@pushly/aqe/lib/components'
import type { ButtonAction } from '@pushly/aqe/lib/components'
import { AppService } from '@halo/services/app.service'
import { Table, Input, Modal, Flex } from 'antd'
import { AntdTableEmptyPlaceholder } from '@halo/components/antd-table-empty-placeholder/antd-table-empty-placeholder'
import EditPageAnalysisConfigDrawer from '@halo/components/edit-page-analysis-config-drawer/edit-page-analysis-config-drawer'
import React from 'react'
import { simpleNotification } from '@pushly/aqe/lib/utils'
import { noop } from '@pushly/aqe/lib/utils/noop'
import './style/page-analysis-configs-list.page.scss'
import {
    DeleteOutlined,
    EditOutlined,
    FileSearchOutlined,
    Loading3QuartersOutlined,
} from '@ant-design/icons'
import { IApiListResponse } from '@pushly/aqe/lib/interfaces'
import SwSimpleLinkPagination from '@halo/components/sw-simple-link-pagination/sw-simple-link-pagination'

function PageAnalysisConfigsListPage() {
    const appService = Container.get(AppService)
    const pageAnalysisConfigsService: PageAnalysisConfigsService = Container.get(PageAnalysisConfigsService)

    const [isLoading, setIsLoading] = useState(false)
    const [searchResponse, setSearchResponse] = useState<IApiListResponse<IHydratedPageAnalysisConfig>>()
    const [newPageAnalysisConfig, setNewPageAnalysisConfig] = useState(null)
    const [search, setSearch] = useState(null)
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 15,
    })

    useEffect(() => {
        fetchResults()
    }, [pagination, search])

    async function fetchResults() {
        setIsLoading(true)

        const results = await pageAnalysisConfigsService.fetchAll({
            query: {
                hydrated: true,
                search: search || undefined,
                limit: pagination.limit,
                page: pagination.page,
            },
        })

        setSearchResponse(results as IApiListResponse<IHydratedPageAnalysisConfig>)
        setIsLoading(false)
    }

    function render() {
        return (
            <div className={classnames('page', 'page-analysis-configs')}>
                <div className={classnames('page-wrapper')}>
                    <PageHeader
                        title="Page Analysis Configs"
                        onTitleSet={noop}
                        action={
                            <Button
                                type="primary"
                                onClick={() => {
                                    setNewPageAnalysisConfig({
                                        is_default: false,
                                        enabled: false,
                                    })
                                }}
                            >
                                New Page Analysis Config
                            </Button>
                        }
                    />
                    {renderTable()}
                </div>
            </div>
        )
    }

    function renderRowActions(_: any, record: IHydratedPageAnalysisConfig): React.ReactNode {
        const dropdownActions: ButtonAction[] = record.is_default
            ? []
            : [
                  {
                      key: 'delete',
                      text: 'Delete',
                      icon: <DeleteOutlined />,
                      onClick: () => del(record),
                  },
              ]

        return (
            <Button
                size="small"
                onClick={() => {
                    appService.route(`/page-analysis-configs/${record.id}`)
                }}
                icon={<EditOutlined />}
                actions={dropdownActions.length > 0 ? dropdownActions : undefined}
            >
                Edit
            </Button>
        )
    }

    function del(pageAnalysisConfig: IHydratedPageAnalysisConfig) {
        Modal.confirm({
            title: 'Delete Page Analysis Config',
            content: (
                <>
                    <p>Are you sure you want to delete the following page analysis config?</p>
                    <p>
                        <b>Name:</b> {pageAnalysisConfig.name}
                        <br />
                        <b>ID:</b> {pageAnalysisConfig.id}
                        <br />
                    </p>
                </>
            ),
            onOk: async (...args: any[]) => {
                const res = await pageAnalysisConfigsService.delete(pageAnalysisConfig.id)

                if (res.ok) {
                    fetchResults()

                    simpleNotification('success', 'Page Analysis Config Deleted')
                } else {
                    simpleNotification('error', res.error.message)
                }
            },
            onCancel: () => {},
        })
    }

    function renderTable() {
        const tableHeader = (
            <Flex className="page-analysis-configs-list-header" gap={8} justify="space-between" align="center">
                <Flex flex={1} gap={8} align="center">
                    <Input.Search
                        className="page-analysis-configs-search"
                        style={{ width: 300 }}
                        size="middle"
                        placeholder="Search ..."
                        enterButton={true}
                        onSearch={(q) => {
                            setSearch(q)
                        }}
                    />
                </Flex>
            </Flex>
        )

        return (
            <>
                <Table<IHydratedPageAnalysisConfig>
                    className="aqe-table-well"
                    title={() => tableHeader}
                    size="small"
                    bordered={false}
                    dataSource={searchResponse?.data}
                    pagination={false}
                    footer={(currentTableData) => (
                        <SwSimpleLinkPagination
                            currentTableData={currentTableData}
                            links={(searchResponse as any)?.links ?? {}}
                            onPrev={(_ev, _link, config) => {
                                setPagination({
                                    ...pagination,
                                    page: config.current!,
                                })
                            }}
                            onNext={(_ev, _link, config) => {
                                setPagination({
                                    ...pagination,
                                    page: config.current!,
                                })
                            }}
                        />
                    )}
                    rowKey={(record) => record.id.toString()}
                    loading={
                        isLoading && {
                            indicator: <Loading3QuartersOutlined spin={true} />,
                        }
                    }
                    locale={{
                        emptyText: (
                            <AntdTableEmptyPlaceholder
                                text="No Page Analysis Configs"
                                icon={<FileSearchOutlined />}
                            />
                        ),
                    }}
                >
                    <Table.Column className="id" title="ID" dataIndex="id" />
                    <Table.Column className="name" title="Name" dataIndex="name" />
                    <Table.Column className="extends" title="Extends" dataIndex="parent_name" />
                    <Table.Column
                        className="field-configs"
                        title={() => {
                            return (
                                <>
                                    <span className="column-name">Field Configs</span>
                                    <br />
                                    <span className="column-name-details">(Local / Inherited)</span>
                                </>
                            )
                        }}
                        render={(text, record: IHydratedPageAnalysisConfig, idx) => (
                            <>
                                {record.fields.filter((a) => a.page_analysis_config_id === record.id).length}/{' '}
                                {record.fields.filter((a) => a.page_analysis_config_id !== record.id).length}
                            </>
                        )}
                    />
                    <Table.Column
                        className="associated-domain"
                        title="Associated Domain"
                        render={(text, record: IHydratedPageAnalysisConfig, idx) => (
                            <>
                                {record.is_default ? (
                                    <i>Default</i>
                                ) : (
                                    <span>{record.associated_domains.map((ad) => ad.name).join(', ')}</span>
                                )}
                            </>
                        )}
                    />
                    <Table.Column className="actions" title="Actions" width={180} render={renderRowActions} />
                </Table>

                <EditPageAnalysisConfigDrawer
                    pageAnalysisConfig={newPageAnalysisConfig}
                    onClose={() => setNewPageAnalysisConfig(null)}
                    onUpdate={(created) => {
                        setNewPageAnalysisConfig(null)
                        // The list-page drawer is creates-only (row edit routes
                        // straight to the details page), so drop the user on the
                        // new config so they can start configuring fields/catalog
                        // without an extra click.
                        if (created?.id) {
                            appService.route(`/page-analysis-configs/${created.id}`)
                        } else {
                            fetchResults()
                        }
                    }}
                />
            </>
        )
    }

    return render()
}

export default PageAnalysisConfigsListPage
